import { useState } from "react";
import { useParams, useRouteContext } from "@tanstack/react-router";
import { Tab, Tabs } from "react-bootstrap";

import { useLeaData } from "@/queries.ts";
import LayoutContentArea from "@/components/layout/LayoutContentArea";
import ClassroomIndividualHeading from "@/components/records/classroom/ClassroomIndividualHeading";
import CLRMRecordsTab from "@/components/records/classroom/tabs/CLRMRecordsTab";
import CLENRecordsTab from "@/components/records/classroom/tabs/CLENRecordsTab";
import SCLRRecordsTab from "@/components/records/classroom/tabs/SCLRRecordsTab";
import Breadcrumb from "@/components/shared/BreadCrumb";

export default function ClassroomPage() {
  const leaContext = useRouteContext({ from: "/$leaid" });

  const params = useParams({
    from: "/$leaid/_error-wrapper/records/classroom/$entityid",
  });
  const classroomId = params.entityid as string;
  const leaid = params.leaid as string;

  // Get data for the classroom, staff, and enrollment records associated with the classroom
  const leaData = useLeaData(params.leaid as string)?.data;

  const clrmRecords = leaData.entities.classrooms[params.entityid].records.map(
    (record) => {
      return {
        ...record,
        PreschoolName: leaContext?.currentOrgPreschools?.find(
          (x) => x.PreschoolCDSCode === record.PreschoolCode
        )?.PreschoolName,
      };
    }
  );

  clrmRecords.sort((x, y) => y.ProgramYear.localeCompare(x.ProgramYear));

  const classroom = clrmRecords[0];

  if (!classroom) {
    throw new Error(`No classroom records exist for this classroom ID`);
  }

  const sclrRecords = classroom?.StaffAssignments?.map((record) => {
    const staff = leaData?.entities.staff[record.LocalStaffId]?.records[0];
    return {
      ...record,
      PreschoolName: leaContext?.currentOrgPreschools?.find(
        (x) => x.PreschoolCDSCode === record.PreschoolCode
      )?.PreschoolName,
      StaffName: staff
        ? [
            staff.StaffLegalFirstName,
            staff.StaffLegalMiddleName,
            staff.StaffLegalLastName,
          ].join(" ")
        : record.LocalStaffId,
    };
  });

  const clenRecords = classroom?.ChildAssignments?.map((record) => {
    const child = leaData?.chld?.find((x) => x.CSPPID === record.CSPPID);
    return {
      ...record,
      PreschoolName: leaContext?.currentOrgPreschools?.find(
        (x) => x.PreschoolCDSCode === record.PreschoolCode
      )?.PreschoolName,
      ChildName: child
        ? [
            child.ChildFirstName,
            child.ChildMiddleName,
            child.ChildLastName,
            child.ChildNameSuffix,
          ].join(" ")
        : record.LocalChildId,
    };
  });

  const classroomName = classroom?.ClassroomName || classroomId;

  const breadcrumbItems = [
    {
      label: "Home",
      to: "/$leaid" as const,
      params: { leaid },
      ariaLabel: "Go to Home",
      isHome: true,
    },
    {
      label: "Classroom Records",
      to: "/$leaid/records/classroom" as const,
      params: { leaid },
    },
    {
      label: classroomName,
    },
  ];

  // Manage tabs state to render & size content only when the tab is active
  const [activeTab, setActiveTab] = useState("clrm");

  return (
    <LayoutContentArea
      headingText={null}
      noBorder={true}
      showHeadingSection={false}
    >
      <Breadcrumb items={breadcrumbItems} />

      <div
        className="card rounded-4 border-0 mb-4"
        style={{
          boxShadow:
            "0 1px 3px 0 rgb(15 23 42 / 0.08), 0 1px 2px -1px rgb(15 23 42 / 0.06)",
        }}
      >
        <div className="card-custom shadow-none p-0">
          <div className="p-3 p-md-4">
            <ClassroomIndividualHeading />
          </div>

          {/* Find alternate solution to grid size calculations that doesn't require re-rendering whole tab contents each time tab changes */}
          <Tabs
            className="section-tabs"
            fill
            activeKey={activeTab}
            onSelect={(key) => setActiveTab(key || "")}
          >
            {leaContext.hasRole("CLRMView") && (
              <Tab eventKey="clrm" title="Classroom (CLRM)" className="p-4">
                {activeTab === "clrm" && (
                  <CLRMRecordsTab clrmRecords={clrmRecords} />
                )}
              </Tab>
            )}
            {leaContext.hasRole("SCLRView") && sclrRecords !== undefined && (
              <Tab
                eventKey="sclr"
                title="Staff Classroom (SCLR)"
                className="p-4"
              >
                {activeTab === "sclr" && (
                  <SCLRRecordsTab
                    sclrRecords={sclrRecords}
                    classroomName={classroom.ClassroomName}
                  />
                )}
              </Tab>
            )}
            {leaContext.hasRole("CLENView") && clenRecords !== undefined && (
              <Tab
                eventKey="clen"
                title="Classroom Enrollment (CLEN)"
                className="p-4"
              >
                {activeTab === "clen" && (
                  <CLENRecordsTab
                    clenRecords={clenRecords}
                    classroomName={classroom.ClassroomName}
                  />
                )}
              </Tab>
            )}
          </Tabs>
        </div>
      </div>
    </LayoutContentArea>
  );
}
